import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Upload,
  Play,
  Pause,
  Volume2,
  HelpCircle,
  Search,
  Filter,
  Eye
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const AdminFAQ = () => {
  const { user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSection, setFilterSection] = useState('');

  const [faqForm, setFaqForm] = useState({
    title: '',
    section: 'getting-started',
    body_html: '',
    tags: [],
    audio_asset_id: null
  });

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/faq`);
      if (response.ok) {
        const data = await response.json();
        setFaqs(data.faqs || []);
        setSections(data.sections || []);
      }
    } catch (error) {
      console.error('Error loading FAQs:', error);
      toast.error('Failed to load FAQ entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFaq = async () => {
    if (!faqForm.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setLoading(true);
    try {
      const url = editingFaq 
        ? `${API_ENDPOINTS.ADMIN}/faq/${editingFaq.id}`
        : `${API_ENDPOINTS.ADMIN}/faq`;
      
      const method = editingFaq ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(faqForm)
      });

      if (response.ok) {
        toast.success(`FAQ ${editingFaq ? 'updated' : 'created'} successfully`);
        resetForm();
        loadFAQs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save FAQ');
      }
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!confirm('Are you sure you want to delete this FAQ entry?')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/faq/${faqId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('FAQ deleted successfully');
        loadFAQs();
      } else {
        toast.error('Failed to delete FAQ');
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const handleEditFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      title: faq.title,
      section: faq.section,
      body_html: faq.body_html,
      tags: faq.tags || [],
      audio_asset_id: faq.audio_asset_id
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setFaqForm({
      title: '',
      section: 'getting-started',
      body_html: '',
      tags: [],
      audio_asset_id: null
    });
    setEditingFaq(null);
    setShowEditor(false);
  };

  const handleTagsChange = (tagsString) => {
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFaqForm(prev => ({ ...prev, tags }));
  };

  const handleAudioUpload = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/faq/upload-audio`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setFaqForm(prev => ({ ...prev, audio_asset_id: data.asset_id }));
        toast.success('Audio uploaded successfully');
      } else {
        toast.error('Failed to upload audio');
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      toast.error('Failed to upload audio');
    }
  };

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = !searchQuery || 
      faq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.body_html.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSection = !filterSection || faq.section === filterSection;
    
    return matchesSearch && matchesSection;
  });

  const groupedFAQs = sections.reduce((acc, section) => {
    acc[section] = filteredFAQs.filter(faq => faq.section === section);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">FAQ Management</h1>
          <p className="text-gray-600 mt-1">
            Manage frequently asked questions and help content
          </p>
        </div>
        
        <Button 
          onClick={() => setShowEditor(!showEditor)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          {showEditor ? 'Cancel' : 'New FAQ Entry'}
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search FAQ entries..."
            className="pl-10"
          />
        </div>
        
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <select
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-white"
          >
            <option value="">All Sections</option>
            {sections.map(section => (
              <option key={section} value={section}>
                {section.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* FAQ Editor */}
      {showEditor && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>
              {editingFaq ? 'Edit FAQ Entry' : 'Create New FAQ Entry'}
            </CardTitle>
            <CardDescription>
              Add or update frequently asked questions for your users
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={faqForm.title}
                  onChange={(e) => setFaqForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter FAQ question..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="section">Section</Label>
                <select
                  id="section"
                  value={faqForm.section}
                  onChange={(e) => setFaqForm(prev => ({ ...prev, section: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {sections.map(section => (
                    <option key={section} value={section}>
                      {section.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="body">Answer Content</Label>
              <Textarea
                id="body"
                value={faqForm.body_html}
                onChange={(e) => setFaqForm(prev => ({ ...prev, body_html: e.target.value }))}
                placeholder="Enter the detailed answer... (HTML supported)"
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                HTML tags are supported for rich formatting (bold, links, lists, etc.)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={faqForm.tags.join(', ')}
                onChange={(e) => handleTagsChange(e.target.value)}
                placeholder="e.g., registration, basic, phase1"
              />
            </div>

            <div className="space-y-2">
              <Label>Audio Explanation (Optional)</Label>
              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAudioUpload}
                  disabled={loading}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {faqForm.audio_asset_id ? 'Replace Audio' : 'Upload Audio'}
                </Button>
                
                {faqForm.audio_asset_id && (
                  <div className="flex items-center space-x-2">
                    <Volume2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">Audio attached</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Upload MP3 audio for detailed explanations (max 10MB)
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={resetForm}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSaveFaq} disabled={loading} className="btn-primary">
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : (editingFaq ? 'Update FAQ' : 'Create FAQ')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{faqs.length}</div>
              <div className="text-sm text-gray-600">Total FAQs</div>
            </div>
          </CardContent>
        </Card>
        
        {sections.slice(0, 3).map(section => (
          <Card key={section}>
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {groupedFAQs[section]?.length || 0}
                </div>
                <div className="text-sm text-gray-600 capitalize">
                  {section.replace('-', ' ')}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* FAQ List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading FAQ entries...</p>
        </div>
      ) : (
        <div className="space-y-8">
          {sections.map(section => {
            const sectionFAQs = groupedFAQs[section] || [];
            if (sectionFAQs.length === 0) return null;

            return (
              <div key={section}>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 capitalize">
                  {section.replace('-', ' ')} ({sectionFAQs.length})
                </h2>
                
                <div className="grid grid-cols-1 gap-4">
                  {sectionFAQs.map(faq => (
                    <Card key={faq.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{faq.title}</CardTitle>
                            <CardDescription className="mt-2">
                              <div 
                                className="line-clamp-2"
                                dangerouslySetInnerHTML={{ 
                                  __html: faq.body_html.substring(0, 150) + '...' 
                                }}
                              />
                            </CardDescription>
                            
                            {faq.tags && faq.tags.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {faq.tags.map(tag => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4">
                            {faq.audio_asset_id && (
                              <Badge variant="outline" className="text-xs">
                                <Volume2 className="h-3 w-3 mr-1" />
                                Audio
                              </Badge>
                            )}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditFaq(faq)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteFaq(faq.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}

          {filteredFAQs.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQ entries found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery || filterSection 
                    ? 'No FAQs match your current filters.'
                    : 'Create your first FAQ entry to help users understand your platform.'
                  }
                </p>
                <Button onClick={() => setShowEditor(true)} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First FAQ
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminFAQ;